// /src/utils/aiPromptTemplates.js
// Helpers that build the AI prompts users copy and paste into their own AI tools
// (ChatGPT, Claude, Gemini, etc.) — tag generation for articles/groups, and the
// Events Importer's freguesia-list and date-window helpers (added 11 Aug 2026).
// Both format in the shared language rule's locale (#85: en → en-GB, others their own code).

import { toFormattingLocale } from '../i18n/resolveLanguage';

/**
 * Generate a structured prompt for AI tag generation based on article content
 * @param {Object} articleData - Article data with title, summary, content
 * @returns {string} Formatted prompt ready to copy to clipboard
 */
export const generateArticleTagPrompt = (articleData) => {
  const { title, summary, content } = articleData;

  return `I'm writing an article for OurNet, a locality-focused social platform in Portugal. Please suggest 10-15 relevant tags for this article. Tags should be:

- In ENGLISH (not Portuguese)
- UPPERCASE format (e.g., "LOCAL_NEWS", "ENVIRONMENT", "SPORTS")
- Focused on: topic category, local relevance, audience type
- Suitable for search and discovery

**Article Details:**

Title: ${title || '[No title yet]'}

Introduction: ${summary || '[No introduction yet]'}

Content: ${content || '[No content yet]'}

**Instructions:**
1. Provide 10-15 tags as a comma-separated list
2. First 10 tags should be primary topics
3. Last 5 can be secondary/related topics
4. Use underscores for multi-word tags (e.g., "COMMUNITY_EVENT")
5. Focus on searchability and relevance

Please respond ONLY with the comma-separated tag list, nothing else.`;
};

/**
 * Generate a structured prompt for AI tag generation based on group/community content
 * @param {Object} groupData - Group data with name, description, landingPageContent
 * @returns {string} Formatted prompt ready to copy to clipboard
 */
export const generateGroupTagPrompt = (groupData) => {
  const { name, description, landingPageContent } = groupData;

  return `I'm creating a community group on OurNet, a locality-focused social platform in Portugal. Please suggest 10-15 relevant tags for this group. Tags should be:

- In ENGLISH (not Portuguese)
- UPPERCASE format (e.g., "LOCAL_COMMUNITY", "SPORTS", "CULTURE")
- Focused on: topic category, activities, interests, group purpose
- Suitable for search and discovery

**Group Details:**

Name: ${name || '[No name yet]'}

Description: ${description || '[No description yet]'}

Landing Page Content: ${landingPageContent || '[No landing page content yet]'}

**Instructions:**
1. Provide 10-15 tags as a comma-separated list
2. First 10 tags should be primary topics/categories
3. Last 5 can be secondary/related topics
4. Use underscores for multi-word tags (e.g., "OUTDOOR_ACTIVITIES")
5. Focus on searchability and what potential members would look for

Please respond ONLY with the comma-separated tag list, nothing else.`;
};

/**
 * Locale-correct conjunction join of freguesia names ("A, B and C" / "A, B e C").
 * Written to take an array from the start (not a single name) — Phase 5a's
 * picker is single-select, but the events-importer instruction panel that
 * calls this must already tolerate a set (Phase 5a Addendum 1 §4; Phase 5b
 * makes the batch itself multi-freguesia).
 * @param {string[]} names
 * @param {string} locale - e.g. 'en', 'pt-PT'
 * @returns {string}
 */
export const formatFreguesiaList = (names, locale) => {
  if (!names || names.length === 0) return '';
  if (typeof Intl !== 'undefined' && Intl.ListFormat) {
    return new Intl.ListFormat(toFormattingLocale(locale), { style: 'long', type: 'conjunction' }).format(names);
  }
  return names.join(', ');
};

/**
 * Explicit end-of-month date range for the simple importer's compiled instruction
 * (docs/briefs/2026-08-11-simple-importer-design.md §4) — an AI reads an explicit
 * date more reliably than a named window ("this month"), and whole-month
 * boundaries are how a steward thinks. The steward never sees these computed
 * dates, only the rendered text inside a translated sentence (the "between … and
 * …" wording lives in the `data_types_window_intro` translation key, not here —
 * this returns bare, locale-formatted date text only).
 *
 * THE OFFSET (ruled by Michael, 11 Aug 2026 — resolves the design doc's own
 * prose-vs-worked-example disagreement, flagged when this was first built):
 * from today, go forward `monthsAhead` whole months, then round out to the
 * last day of that month — 1 → end of next month, 2 → end of the month after,
 * 3 → end of the month after that (e.g. "3" on 11 Aug 2026 → 30 Nov 2026,
 * matching the design doc's worked example).
 *
 * @param {number} monthsAhead - 1, 2, or 3 (ticked by the steward)
 * @param {string} locale - 'en' | 'pt-PT'
 * @param {Date} [today] - injectable for tests; defaults to now
 * @returns {{ startText: string, endText: string }}
 */
export const computeInstructionWindow = (monthsAhead, locale, today = new Date()) => {
  const fmt = new Intl.DateTimeFormat(toFormattingLocale(locale), { day: 'numeric', month: 'long', year: 'numeric' });
  const endMonthIndex = today.getMonth() + monthsAhead;
  const endOfFinalMonth = new Date(today.getFullYear(), endMonthIndex + 1, 0); // day 0 = last day of the target month
  return {
    startText: fmt.format(today),
    endText: fmt.format(endOfFinalMonth)
  };
};
